import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { buildApiUrl } from "../../lib/api";
import "./SavvyInsight.css";

// ------------------------------------------------------------------ //
// Sub-components                                                       //
// ------------------------------------------------------------------ //

function InsightSkeleton() {
    return (
        <div className="si-skeleton-wrap">
            <div className="si-skeleton-header">
                <div className="si-skeleton" style={{ width: 100, height: 10 }} />
                <div className="si-skeleton" style={{ width: 50, height: 18, borderRadius: 20 }} />
            </div>
        </div>
    );
}

function SimConfidenceBars({ simData }) {
    if (!simData) return null;
    const { prob_of_profit, prob_of_ruin } = simData;

    const profitColor =
        prob_of_profit >= 0.70 ? "#4ade80" :
        prob_of_profit >= 0.55 ? "#fbbf24" : "#f87171";

    const ruinColor =
        prob_of_ruin <= 0.05 ? "#4ade80" :
        prob_of_ruin <= 0.15 ? "#fbbf24" : "#f87171";

    return (
        <div className="si-sim-bars">
            {/* Prob of profit bar */}
            <div className="si-sim-bar-row">
                <span className="si-sim-bar-label">Probability of profit</span>
                <div className="si-sim-bar-track">
                    <motion.div
                        className="si-sim-bar-fill"
                        style={{ background: profitColor }}
                        initial={{ width: 0 }}
                        animate={{ width: `${prob_of_profit * 100}%` }}
                        transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
                    />
                </div>
                <span className="si-sim-bar-value" style={{ color: profitColor }}>
                    {(prob_of_profit * 100).toFixed(0)}%
                </span>
            </div>
            {/* Drawdown risk bar */}
            <div className="si-sim-bar-row">
                <span className="si-sim-bar-label">Drawdown risk</span>
                <div className="si-sim-bar-track">
                    <motion.div
                        className="si-sim-bar-fill"
                        style={{ background: ruinColor }}
                        initial={{ width: 0 }}
                        animate={{ width: `${prob_of_ruin * 100}%` }}
                        transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                    />
                </div>
                <span className="si-sim-bar-value" style={{ color: ruinColor }}>
                    {(prob_of_ruin * 100).toFixed(1)}%
                </span>
            </div>
        </div>
    );
}

// ------------------------------------------------------------------ //
// Component                                                            //
// ------------------------------------------------------------------ //

const SUPPORTED_PREVIEW_ACTIONS = new Set([
    "SELL_CASH_SECURED_PUT",
    "SELL_PUT",
]);

const SavvyInsight = ({ decision, symbol, portfolioValue, strategy = "wheel" }) => {
    const [insight,  setInsight]  = useState(null);
    const [simData,  setSimData]  = useState(null);
    const [loading,  setLoading]  = useState(false);
    const [open,     setOpen]     = useState(true);

    const fetchData = useCallback(async () => {
        const meta  = decision?.meta;
        const price = decision?.price;
        const isWheelLikeTrade =
            strategy === "wheel" || SUPPORTED_PREVIEW_ACTIONS.has(decision?.action);

        if (!decision?.should_trade || !meta || price == null || !isWheelLikeTrade) {
            setInsight(null);
            setSimData(null);
            return;
        }

        const { strike_price, expiration_date, delta, dte } = meta;
        if (!strike_price || !price || delta == null || !dte) {
            setInsight(null);
            setSimData(null);
            return;
        }

        setLoading(true);
        setInsight(null);
        setSimData(null);

        // Fire both requests in parallel
        const brainReq = axios.post(
            buildApiUrl("/brain/analyze"),
            {
                trade: {
                    symbol,
                    strike:           Number(strike_price),
                    expiration:       expiration_date || "",
                    delta:            Number(delta ?? 0),
                    premium:          Number(price),
                    capital_required: Number(strike_price) * 100,
                    strategy:         "wheel",
                },
                account: {
                    portfolio_value: portfolioValue ?? 0,
                    buying_power:    portfolioValue ?? 0,
                },
            },
            { withCredentials: true }
        );

        const simReq = axios.post(
            buildApiUrl("/simulation/trade-preview"),
            {
                strike:          Number(strike_price),
                delta:           Number(delta ?? 0),
                premium:         Number(price),
                dte:             Number(dte ?? 7),
                initial_capital: portfolioValue ?? 1000,
            },
            { withCredentials: true }
        );

        try {
            const [brainRes, simRes] = await Promise.allSettled([brainReq, simReq]);
            if (brainRes.status === "fulfilled") setInsight(brainRes.value.data);
            if (simRes.status   === "fulfilled") setSimData(simRes.value.data);
            setOpen(true);
        } finally {
            setLoading(false);
        }
    }, [decision, portfolioValue, strategy, symbol]);

    useEffect(() => { fetchData(); }, [fetchData]);

    if (loading) return <InsightSkeleton />;
    if (!insight && !simData) return null;

    return (
        <div className="savvy-insight">
            <button className="si-toggle" onClick={() => setOpen((o) => !o)}>
                <span className="si-toggle-label">Savvy Insight</span>
                <div className="si-toggle-right">
                    {insight && (
                        <span className={`si-confidence si-confidence--${insight.confidence}`}>
                            {insight.confidence}
                        </span>
                    )}
                    <span className="si-arrow">{open ? "↑" : "↓"}</span>
                </div>
            </button>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        className="si-body"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.22, ease: "easeOut" }}
                        style={{ overflow: "hidden" }}
                    >
                        {/* Simulation-backed confidence bars */}
                        <SimConfidenceBars simData={simData} />

                        {insight && (
                            <>
                                {/* Reasoning bullets */}
                                <ul className="si-reasoning">
                                    {insight.reasoning.map((r, i) => (
                                        <li key={i}>{r}</li>
                                    ))}
                                </ul>

                                {/* Stats row */}
                                <div className="si-stats">
                                    <div className="si-stat">
                                        <span className="si-stat-label">Duration</span>
                                        <span className="si-stat-value">{insight.expected_duration_days}d</span>
                                    </div>
                                    <div className="si-stat-divider" />
                                    <div className="si-stat">
                                        <span className="si-stat-label">Risk</span>
                                        <span className={`si-stat-value si-risk--${insight.risk_level}`}>
                                            {insight.risk_level}
                                        </span>
                                    </div>
                                    <div className="si-stat-divider" />
                                    <div className="si-stat">
                                        <span className="si-stat-label">ROC</span>
                                        <span className="si-stat-value si-roc">
                                            {(insight.capital_efficiency * 100).toFixed(2)}%
                                        </span>
                                    </div>
                                </div>

                                {/* Warnings */}
                                {insight.warnings?.length > 0 && (
                                    <div className="si-warnings">
                                        {insight.warnings.map((w, i) => (
                                            <div key={i} className="si-warning">
                                                <span className="si-warning-icon">⚠</span>
                                                {w}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Suggestions */}
                                {insight.suggestions?.length > 0 && (
                                    <div className="si-suggestions">
                                        {insight.suggestions.map((s, i) => (
                                            <div key={i} className="si-suggestion">
                                                <span className="si-suggestion-icon">→</span>
                                                {s}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SavvyInsight;
